// stores/useAuthStore.ts
import { create } from "zustand";
import { persist } from "zustand/middleware";

const toLegacyBranchKey = (key: string): string =>
  key.replace(/_(\w)/g, (_, letter) => letter.toUpperCase());

interface AuthStore {
  user: any | null;
  token: string | null;
  permissions: Record<string, any> | null;
  setAuth: (data: { user: any; token?: string | null }) => void;
  logout: () => void;
  isSuperAdmin: () => boolean;
  hasPermission: (
    branch: string,
    path: string,
    module: string,
    action: "read" | "create" | "update" | "delete",
  ) => boolean;
  getAllModules: (branch: string) => string[];
  hasAccess: (
    module: "academies" | "dining" | "assetsRental" | "performancePlus",
    id: string,
  ) => boolean;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      permissions: null,
      setAuth: ({ user, token }) => {
        set((state) => ({
          user,
          token:
            token !== undefined && token !== null && token !== ""
              ? token
              : state.token,
          permissions: user?.permissions ?? {},
        }));
      },
      logout: () => {
        set({ user: null, token: null, permissions: null });
      },
      isSuperAdmin: () => {
        const { user } = get();
        return user?.isSuperadmin === true;
      },
      hasPermission: (branch, path, module, action) => {
        const { user, permissions } = get();
        // If user is superadmin, return true for all permissions
        if (user?.isSuperadmin === true) {
          return true;
        }

        if (!permissions || !branch) {
          return false;
        }

        const legacyKey = toLegacyBranchKey(branch);
        const branchNode =
          permissions[branch] ??
          (legacyKey !== branch ? permissions[legacyKey] : undefined);
        return Boolean(branchNode?.[path]?.[module]?.[action]);
      },
      getAllModules: (branch: string) => {
        const permissions = get().permissions;
        if (!permissions || !branch) return [];
        const legacyKey = toLegacyBranchKey(branch);
        const node =
          permissions[branch] ??
          (legacyKey !== branch ? permissions[legacyKey] : undefined);
        if (!node) return [];
        return Object.keys(node);
      },
      hasAccess: (module, id) => {
        const user = get().user;

        // Superadmin has access to everything
        if (user?.isSuperadmin === true) return true;

        // Basic safety checks
        if (!user || !user.accesses || !user.accesses[module]) return false;

        const moduleAccess = user.accesses[module];

        // Grant access if the module includes "ALL" or the specific id
        return moduleAccess.includes("ALL") || moduleAccess.includes(id);
      },
    }),
    {
      name: "auth-storage", // saved to localStorage
    },
  ),
);
